/**
 * Liste des charges fixes avec actions - Style Centre de Pilotage
 * @module components/tresorerie/ChargesFixesList
 */

import { useState } from 'react';
import {
  Home,
  Shield,
  Car,
  FileText,
  Zap,
  Fuel,
  Landmark,
  Building2,
  FileSpreadsheet,
  Pin,
  Pencil,
  Trash2,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { ChargeFix, CategorieChargeFix } from '@/types/charges-fixes.types';
import { FREQUENCES_SUFFIXES } from '@/types/charges-fixes.types';
import { cn } from '@/lib/utils';

interface ChargesFixesListProps {
  chargesFixes: ChargeFix[];
  onEdit: (charge: ChargeFix) => void;
  onDelete: (id: string) => void;
  onToggleActif: (id: string, actif: boolean) => void;
}

// Mapping des icônes par catégorie
const CATEGORY_ICONS: Record<CategorieChargeFix, React.ElementType> = {
  loyer: Home,
  assurance: Shield,
  leasing: Car,
  abonnement: FileText,
  energie: Zap,
  vehicules: Fuel,
  emprunt: Landmark,
  comptable: Building2,
  taxes: FileSpreadsheet,
  autre: Pin,
};

// Couleurs par catégorie pour le style pilotage
const CATEGORY_COLORS: Record<CategorieChargeFix, { bg: string; icon: string; border: string }> = {
  loyer: { bg: 'bg-orange-500/10', icon: 'text-orange-400', border: 'border-orange-500/20' },
  assurance: { bg: 'bg-blue-500/10', icon: 'text-blue-400', border: 'border-blue-500/20' },
  leasing: { bg: 'bg-purple-500/10', icon: 'text-purple-400', border: 'border-purple-500/20' },
  abonnement: { bg: 'bg-cyan-500/10', icon: 'text-cyan-400', border: 'border-cyan-500/20' },
  energie: { bg: 'bg-yellow-500/10', icon: 'text-yellow-400', border: 'border-yellow-500/20' },
  vehicules: { bg: 'bg-red-500/10', icon: 'text-red-400', border: 'border-red-500/20' },
  emprunt: { bg: 'bg-emerald-500/10', icon: 'text-emerald-400', border: 'border-emerald-500/20' },
  comptable: { bg: 'bg-indigo-500/10', icon: 'text-indigo-400', border: 'border-indigo-500/20' },
  taxes: { bg: 'bg-pink-500/10', icon: 'text-pink-400', border: 'border-pink-500/20' },
  autre: { bg: 'bg-gray-500/10', icon: 'text-gray-400', border: 'border-gray-500/20' },
};

/**
 * Formate un montant en euros
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Composant pour une ligne de charge fixe - Style sombre
 */
function ChargeFixeRow({
  charge,
  onEdit,
  onDelete,
  onToggleActif,
}: {
  charge: ChargeFix;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActif: (actif: boolean) => void;
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const Icon = CATEGORY_ICONS[charge.categorie] || Pin;
  const colors = CATEGORY_COLORS[charge.categorie] || CATEGORY_COLORS.autre;

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between p-3 rounded-xl border transition-all',
        charge.actif
          ? `${colors.bg} ${colors.border}`
          : 'bg-white/[0.02] border-white/[0.05] opacity-50'
      )}
    >
      {/* Info principale */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={cn('h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0', colors.bg)}>
          <Icon className={cn('h-4 w-4', colors.icon)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-white truncate">{charge.nom}</span>
            {!charge.actif && (
              <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-white/10 text-white/50">
                Inactif
              </span>
            )}
          </div>
          <p className="text-xs text-white/40">
            Prélèvement le {charge.jour_prelevement}
          </p>
        </div>
      </div>

      {/* Montant */}
      <div className="text-right mr-3">
        <span className="font-semibold text-white">
          {formatCurrency(charge.montant_ht)}
        </span>
        <span className="text-xs text-white/40 ml-1">
          {FREQUENCES_SUFFIXES[charge.frequence]}
        </span>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <Switch
          checked={charge.actif}
          onCheckedChange={onToggleActif}
          aria-label={charge.actif ? 'Désactiver' : 'Activer'}
        />
        <Button
          variant="ghost"
          size="icon"
          onClick={onEdit}
          className="h-8 w-8 text-white/50 hover:text-white hover:bg-white/10"
          aria-label="Modifier"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-400/70 hover:text-red-400 hover:bg-red-500/10"
              aria-label="Supprimer"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-[#0F0F14] border-white/[0.08]">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Supprimer cette charge ?</AlertDialogTitle>
              <AlertDialogDescription className="text-white/60">
                Voulez-vous vraiment supprimer "{charge.nom}" ? Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting} className="border-white/[0.1] text-white/70 hover:bg-white/[0.05]">
                Annuler
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={isDeleting}
                className="bg-red-600 text-white hover:bg-red-700"
              >
                {isDeleting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Suppression...</>
                ) : (
                  "Supprimer"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

/**
 * Liste des charges fixes - Style Centre de Pilotage
 */
export function ChargesFixesList({
  chargesFixes,
  onEdit,
  onDelete,
  onToggleActif,
}: ChargesFixesListProps) {
  if (chargesFixes.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="p-4 rounded-full bg-white/5 inline-block mb-3">
          <Pin className="h-8 w-8 text-white/30" />
        </div>
        <p className="text-white/60 font-medium">Aucune charge fixe enregistrée</p>
        <p className="text-xs text-white/40 mt-1">
          Ajoutez vos dépenses récurrentes (loyer, assurance, etc.)
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {chargesFixes.map((charge) => (
        <ChargeFixeRow
          key={charge.id}
          charge={charge}
          onEdit={() => onEdit(charge)}
          onDelete={() => onDelete(charge.id)}
          onToggleActif={(actif) => onToggleActif(charge.id, actif)}
        />
      ))}
    </div>
  );
}
